import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import CryptoIconSVG from "@/assets/svg/creator-shield/crypto.svg";
import BoostLogo from "@/assets/svg/full-logo/boost-white.svg";
import EnfinityLogo from "@/assets/svg/full-logo/enfinity-white.svg";
import PayLogo from "@/assets/svg/full-logo/pay-white.svg";
import ShieldLogo from "@/assets/svg/full-logo/shield-white.svg";
import MicrosoftIconSVG from "@/assets/svg/microsoft-icon.svg";
import SnapchatIconSVG from "@/assets/svg/snapchat-icon.svg";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import ToastModal from "@/components/ToastModal";
import scrollTo from "@/utils/scrollTo";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const FOOTER_CDN =
  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity";
const FOOTER_BG_DEFAULT = `${FOOTER_CDN}/footer-bg.png`;
const FOOTER_BG_BOOST = `${FOOTER_CDN}/footer-boost.png`;
const FOOTER_BG_SHIELD = `${FOOTER_CDN}/footer-shield.png`;
const PARTNER_LOGO_URL = `${FOOTER_CDN}/partner.png`;
const ENVELOPE_FOOTER = `${FOOTER_CDN}/envelop.png`;
const FOOTER_ADDRESS_LINE1 =
  "18 Whitfield Road, Causeway Bay, Room 1607-8, 16th Floor,";
const FOOTER_ADDRESS_LINE2 = "Wan Guo Baotong Center, Causeway Bay, Hong Kong";
const SOCIAL_ICONS = [
  {
    name: "Facebook",
    icon: `${FOOTER_CDN}/f-facebook.svg`,
    url: "https://www.facebook.com/profile.php?id=61587027306800",
  },
  {
    name: "YouTube",
    icon: `${FOOTER_CDN}/f-youtube.svg`,
    url: "https://www.youtube.com/@enfinity",
  },
  {
    name: "TikTok",
    icon: `${FOOTER_CDN}/f-tiktok.svg`,
    url: "https://www.tiktok.com/@enfinitylimited?is_from_webapp=1&sender_device=pc",
  },
  {
    name: "Instagram",
    icon: `${FOOTER_CDN}/f-instagram.svg`,
    url: "https://www.instagram.com/enfinitylimited?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
  },
  {
    name: "Telegram",
    icon: `${FOOTER_CDN}/f-telegram.svg`,
    url: "https://web.telegram.org/a/#6110481522",
  },
];
export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serviceType = searchParams.get("service");

  const isHome = location.pathname === "/";
  const landingPage = import.meta.env.VITE_LANDING_PAGE;
  const isEnfiniTermsRoute =
    location.pathname === "/terms-of-service" && landingPage !== "enfinity";
  const isEnfiniPay =
    location.pathname.includes("enfinipay") ||
    (location.pathname === "/" && import.meta.env.VITE_LANDING_PAGE === "pay");
  const isEnfiniBoost =
    location.pathname.includes("enfiniboost") ||
    (location.pathname === "/" &&
      import.meta.env.VITE_LANDING_PAGE === "boost") ||
    (location.pathname === "/terms-of-service" &&
      (landingPage === "boost" ||
        (landingPage === "enfinity" && serviceType === "enfiniboost")));
  const isEnfiniShield =
    location.pathname.includes("enfinishield") ||
    (location.pathname === "/" &&
      import.meta.env.VITE_LANDING_PAGE === "shield") ||
    (location.pathname === "/terms-of-service" &&
      (landingPage === "shield" ||
        (landingPage === "enfinity" && serviceType === "enfinishield")));

  const footerLogo = isEnfiniPay
    ? PayLogo
    : isEnfiniBoost
      ? BoostLogo
      : isEnfiniShield
        ? ShieldLogo
        : EnfinityLogo;
  const footerLogoAlt = isEnfiniPay
    ? "EnfiniPay"
    : isEnfiniBoost
      ? "EnfiniBoost"
      : isEnfiniShield
        ? "EnfiniShield"
        : "Enfinity";
  const footerBg = isEnfiniBoost
    ? FOOTER_BG_BOOST
    : isEnfiniShield
      ? FOOTER_BG_SHIELD
      : FOOTER_BG_DEFAULT;
  const contactUsPath = isEnfiniPay
    ? "/our-solutions/enfinipay/contact-us"
    : isEnfiniBoost
      ? "/our-solutions/enfiniboost/contact-us"
      : isEnfiniShield
        ? "/our-solutions/enfinishield/contact-us"
        : "/contact-us";
  const aboutUsPath = isEnfiniPay
    ? "/our-solutions/enfinipay/about-us"
    : isEnfiniBoost
      ? "/our-solutions/enfiniboost/about-us"
      : isEnfiniShield
        ? "/our-solutions/enfinishield/about-us"
        : "/about-us";
  const caseStudiesPath = isEnfiniPay
    ? "/our-solutions/enfinipay/case-studies"
    : isEnfiniBoost
      ? "/our-solutions/enfiniboost/case-studies"
      : isEnfiniShield
        ? "/our-solutions/enfinishield/case-studies"
        : "/case-studies";

  const handleNavClick = (targetPath, scrollId) => {
    if (location.pathname === targetPath) {
      scrollTo(scrollId);
    } else {
      navigate(targetPath, { state: { scrollTo: scrollId } });
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        scrollTo(location.state.scrollTo);
        navigate(location.pathname, { replace: true, state: {} });
      }, 100);
    }
  }, [location, navigate]);

  const badges = [
    {
      name: "Official YouTube Certified",
      icon: YoutubeIconSVG,
      link: "https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/",
    },
    {
      name: "Microsoft Certified Partner",
      icon: MicrosoftIconSVG,
      link: "#",
    },
    {
      name: "Official Snap Partner",
      icon: SnapchatIconSVG,
      link: "#",
    },
    {
      name: "Crypto Payout Agency",
      icon: CryptoIconSVG,
      link: "#",
    },
  ];

  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const current = badges[currentBadgeIndex];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBadgeIndex((prev) => (prev + 1) % badges.length);
    }, 3000); // Change badge every 3 seconds

    return () => clearInterval(interval);
  }, [badges.length]);

  // Placeholder for social platforms - update with actual social platforms if needed
  const socialPlatforms = [];

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState("");
  const [showSubscribeToast, setShowSubscribeToast] = useState(false);
  const [subscribeToastType, setSubscribeToastType] = useState("success");

  const isValidEmail = (email) => {
    const trimmed = (email || "").trim();
    if (!trimmed) return false;
    if (!trimmed.includes("@")) return false;
    if (!trimmed.toLowerCase().includes(".com")) return false;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(trimmed);
  };

  const handleSubscribe = async () => {
    const trimmedEmail = newsletterEmail.trim();
    if (!trimmedEmail) {
      setSubscribeStatus("⚠️ Please enter your email address.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setSubscribeStatus("⚠️ Please enter a valid email with @ and .com");
      return;
    }

    setSubscribeLoading(true);
    setSubscribeStatus("");

    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbwRNGfW4euq_LOuSpAK3UO1eF3AjbmBKl4ycpN7_jofDPTRh5g34c5S6VC_EX90iaGPlw/exec",
        {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ email: trimmedEmail }).toString(),
        }
      );

      const result = await response.json();

      if (result?.result === "already_subscribed") {
        setSubscribeStatus("⚠️ You're already subscribed.");
        setSubscribeToastType("success");
        setShowSubscribeToast(true);
      } else if (result?.result === "success") {
        setSubscribeStatus("✅ Subscribed successfully!");
        setNewsletterEmail("");
        setSubscribeToastType("success");
        setShowSubscribeToast(true);
      } else {
        setSubscribeStatus("⚠️ Something went wrong.");
        setSubscribeToastType("error");
        setShowSubscribeToast(true);
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setSubscribeStatus("⚠️ Network error. Try again later.");
      setSubscribeToastType("error");
      setShowSubscribeToast(true);
    } finally {
      setSubscribeLoading(false);
    }
  };

  return (
    <div className="bg-[#1A1D29] pt-16 lg:pt-20 pb-4 lg:pb-14 px-6 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        {/* Newsletter - overlaps FAQ above (hidden on enfini terms route) */}
        {!isEnfiniTermsRoute && (
        <div
          className="relative z-10 overflow-hidden rounded-[24px] lg:rounded-[28px] px-6 lg:px-12 py-8 lg:py-10 pb-14 lg:pb-10 mb-12 -mt-24 xxs:-mt-64 sm:-mt-52 lg:-mt-[220px] 4xl:-mt-[220px] bg-cover bg-center bg-no-repeat shadow-xl min-h-[460px] lg:min-h-0"
          style={{ backgroundImage: `url(${footerBg})` }}
        >
          {/* Subtle wave/layer pattern overlay */}
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            aria-hidden
          >
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-emerald-900/30 blur-2xl" />
          </div>
          {/* Envelope graphic - right side */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-[20%] pointer-events-none hidden lg:block">
            <img
              src={ENVELOPE_FOOTER}
              alt=""
              className="w-[280px] lg:w-[350px] h-auto object-contain drop-shadow-lg"
              aria-hidden
            />
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-6 relative z-10">
            <div className="flex flex-col gap-5 lg:gap-3 max-w-[520px]">
              <h3 className="text-2xl xxs:text-[16px] xs:text-[22px] lg:text-4xl font-normal leading-tight text-white enfinity-header-font mb-5 xxs:mb-3 lg:mb-4 text-center lg:text-left whitespace-nowrap">
                Subscribe to our newsletter
              </h3>
              <p className="text-xl text-white/50 leading-[1.5] font-normal xxs:text-base enfinity-subtext-font text-center lg:text-left">
                Stay updated on Enfinity's services, creator programs, platform
                updates, and featured success stories.
              </p>
              <div className="flex flex-col md:flex-row gap-4 md:gap-3 mt-4 lg:mt-2">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                  placeholder="Your email address..."
                  className="bg-white rounded-3xl px-5 py-3 text-base text-gray-900 placeholder:text-gray-400 enfinity-subtext-font focus:outline-none focus:ring-2 focus:ring-white/50 w-full md:w-[280px] lg:w-[360px] border-0"
                  disabled={subscribeLoading}
                />
                <button
                  type="button"
                  onClick={handleSubscribe}
                  disabled={subscribeLoading}
                  className="bg-[#363636] text-white font-medium text-sm py-3.5 px-6 rounded-3xl enfinity-subtext-font shrink-0 disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wide border-0 border-b-[0.1px] border-white/40 shadow-none w-fit md:w-auto self-center md:self-auto mt-2 md:mt-0"
                >
                  {subscribeLoading ? "Subscribing..." : "Subscribe"}
                </button>
              </div>
              {subscribeStatus && (
                <p className="text-sm enfinity-subtext-font text-neutral-200 mt-1">
                  {subscribeStatus}
                </p>
              )}
              {/* Envelope - mobile only, below button */}
              <div className="absolute -bottom-60 flex justify-center mt-6 lg:hidden">
                <img
                  src={ENVELOPE_FOOTER}
                  alt=""
                  className="w-[500px] xs:w-[500px] h-auto object-contain drop-shadow-lg"
                  aria-hidden
                />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Main Content */}
        <div className="flex flex-col gap-10 lg:gap-12 mb-12">
          {/* Logo + Address row (desktop): logo left, address right */}
          <div className="hidden lg:flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
            <div className="flex items-center justify-center lg:justify-start">
              <img
                src={footerLogo}
                alt={footerLogoAlt}
                className={`w-auto object-contain ${isEnfiniPay || isEnfiniBoost || isEnfiniShield ? "h-6 lg:h-8" : "h-5 lg:h-6"}`}
              />
            </div>
            <p className="text-sm lg:text-base text-[#737373] enfinity-subtext-font text-center sm:text-right max-w-[500px] sm:max-w-none mt-4">
              {FOOTER_ADDRESS_LINE1}
              <br />
              {FOOTER_ADDRESS_LINE2}
            </p>
          </div>

          {/* Mobile: single column, all centered */}
          <div className="lg:hidden flex flex-col items-center text-center w-full gap-8 pb-6">
            <img
              src={footerLogo}
              alt={footerLogoAlt}
              className={`w-auto object-contain ${isEnfiniPay || isEnfiniBoost || isEnfiniShield ? "h-6" : "h-5"}`}
            />
            <img
              src={PARTNER_LOGO_URL}
              alt="Microsoft and YouTube Partner"
              className="h-10 w-auto object-contain max-w-[220px] -mt-8 xxs:mt-4 4xl:mt-4"
            />
            <p className="text-base text-[#9CA3AF] enfinity-subtext-font leading-6 max-w-[500px]">
              {isEnfiniPay
                ? "Take control of your revenue with flexible payment options designed for creators. From daily payouts to crypto, we give you earlier access to your hard-earned money."
                : isEnfiniBoost
                  ? "Enfiniboost expands content distribution across Microsoft MSN, helping your best videos get discovered and generate additional revenue."
                  : isEnfiniShield
                    ? "Enterprise-grade content protection that detects unauthorized reuploads across platforms and helps creators enforce their rights and monetize eligible matches on YouTube through Content ID."
                    : "The all-in-one revenue and growth platform for YouTube creators. Manage payments, protect content, distribute globally, and hire talent."}
            </p>
            <p className="text-base text-[#BCBCBC] enfinity-subtext-font leading-6 max-w-[500px]">
              Payment services are powered by licensed and regulated financial
              institutions.
            </p>
            <div className="flex flex-col gap-8 w-full max-w-[280px] mx-auto">
              <div>
                <h6 className="text-lg font-normal enfinity-header-font text-[#737373] mb-4">
                  Enfinity Services
                </h6>
                <div className="flex flex-col gap-3 text-base text-white enfinity-subtext-font">
                  <a
                    href="https://enfinipay.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    EnfiniPay
                  </a>
                  <a
                    href="https://enfiniboost.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    EnfiniBoost
                  </a>
                  <a
                    href="https://enfinishield.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    EnfiniShield
                  </a>
                  <a
                    href="https://enfinitask.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    EnfiniTask
                  </a>
                </div>
              </div>
              <div>
                <h6 className="text-lg font-normal enfinity-header-font text-[#737373] mb-4">
                  Quick Links
                </h6>
                <div className="flex flex-col gap-3 text-base text-white enfinity-subtext-font">
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      isHome
                        ? scrollTo("OurServices")
                        : handleNavClick("/", "OurServices")
                    }
                  >
                    Our Services
                  </p>
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      isEnfiniBoost || isEnfiniPay || isEnfiniShield
                        ? location.pathname === aboutUsPath
                          ? scrollTo("about-us")
                          : navigate(aboutUsPath)
                        : isHome
                          ? scrollTo("about-us")
                          : handleNavClick("/", "about-us")
                    }
                  >
                    About Us
                  </p>
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      isEnfiniBoost || isEnfiniPay || isEnfiniShield
                        ? location.pathname === caseStudiesPath
                          ? scrollTo("success-stories")
                          : navigate(caseStudiesPath)
                        : isHome
                          ? scrollTo("success-stories")
                          : handleNavClick("/", "success-stories")
                    }
                  >
                    Case Studies
                  </p>
                </div>
              </div>
              <div>
                <h6 className="text-lg font-normal enfinity-header-font text-[#737373] mb-4">
                  Payments
                </h6>
                <div className="flex flex-col gap-3 text-base text-white enfinity-subtext-font">
                  <a
                    href="https://enfinipay.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Daily Pay
                  </a>
                  <a
                    href="https://enfinipay.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Early Pay
                  </a>
                  <a
                    href="https://enfinipay.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Advance Pay
                  </a>
                  <a
                    href="https://enfinipay.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Crypto Withdrawals
                  </a>
                </div>
              </div>
              <div>
                <h6 className="text-lg font-normal enfinity-header-font text-[#737373] mb-4">
                  Resources
                </h6>
                <div className="flex flex-col gap-3 text-base text-white enfinity-subtext-font">
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      isHome ? scrollTo("faqs") : handleNavClick("/", "faqs")
                    }
                  >
                    FAQs
                  </p>
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      location.pathname === contactUsPath
                        ? scrollTo("get-in-touch")
                        : navigate(`${contactUsPath}#get-in-touch`)
                    }
                  >
                    Contact Us
                  </p>
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      location.pathname === contactUsPath
                        ? scrollTo("get-in-touch")
                        : navigate(`${contactUsPath}#get-in-touch`)
                    }
                  >
                    Support
                  </p>
                </div>
              </div>
            </div>
            <p className="text-sm text-[#9CA3AF] enfinity-subtext-font max-w-[320px] mt-4">
              {FOOTER_ADDRESS_LINE1}
              <br />
              {FOOTER_ADDRESS_LINE2}
            </p>
            <div className="flex flex-col gap-3">
              <p className="text-base text-white enfinity-subtext-font">
                Follow our social media:
              </p>
              <div className="flex flex-wrap gap-3 justify-center items-center">
                {SOCIAL_ICONS.map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`hover:opacity-80 transition-opacity ${platform.name === "Telegram" ? "ml-1" : ""}`}
                    aria-label={platform.name}
                  >
                    <img
                      src={platform.icon}
                      alt=""
                      className={`object-contain ${platform.name === "Telegram" ? "w-9 h-9" : "w-10 h-10"}`}
                      aria-hidden
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="max-lg:hidden flex flex-col lg:flex-row justify-between gap-10 lg:gap-20">
            {/* Left Side - Company Info (partner + copy for all) */}
            <div className="flex flex-col gap-6 max-w-[500px] lg:max-w-[500px] mx-auto lg:mx-0 text-base">
              <div className="flex justify-center lg:justify-start -mt-2">
                <img
                  src={PARTNER_LOGO_URL}
                  alt="Microsoft and YouTube Partner"
                  className="h-12 w-auto object-contain max-w-[260px]"
                />
              </div>
              <p className="text-lg font-normal text-[#737373] leading-6 enfinity-subtext-font text-center lg:text-left">
                {isEnfiniPay
                  ? "Take control of your revenue with flexible payment options designed for creators. From daily payouts to crypto, we give you earlier access to your hard-earned money."
                  : isEnfiniBoost
                    ? "Enfiniboost expands content distribution across Microsoft MSN, helping your best videos get discovered and generate additional revenue."
                    : isEnfiniShield
                      ? "Enterprise-grade content protection that detects unauthorized reuploads across platforms and helps creators enforce their rights and monetize eligible matches on YouTube through Content ID."
                      : "The all-in-one revenue and growth platform for all creators. Manage payments, protect content, distribute globally, and hire talent."}
              </p>
              <p className="text-lg font-normal text-[#737373] leading-6 enfinity-subtext-font text-center lg:text-left">
                Payment services are powered by licensed and regulated financial
                institutions.
              </p>
              <div className="flex flex-col gap-4">
                <p className="text-lg font-normal text-white enfinity-subtext-font text-center lg:text-left">
                  Follow our social media:
                </p>
                <div className="flex flex-wrap gap-2 justify-center lg:justify-start items-center">
                  {SOCIAL_ICONS.map((platform) => (
                    <a
                      key={platform.name}
                      href={platform.url}
                      target={
                        platform.url.startsWith("http") ? "_blank" : undefined
                      }
                      rel={
                        platform.url.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className={`hover:opacity-80 transition-opacity ${platform.name === "Telegram" ? "ml-1" : ""}`}
                      aria-label={platform.name}
                    >
                      <img
                        src={platform.icon}
                        alt=""
                        className={`object-contain ${platform.name === "Telegram" ? "w-[42px] h-[42px]" : "w-12 h-12"}`}
                        aria-hidden
                      />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Navigation Links (main layout: 4 columns for all pages) */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-6 xl:gap-0 1xl:gap-0 4xl:gap-12 text-white w-full lg:max-w-none lg:w-auto lg:ml-auto">
              <div className="grid grid-cols-2 gap-6 lg:gap-x-16 lg:gap-y-6 w-full">
                {/* Enfinity Services */}
                <div className="flex flex-col gap-3 items-center lg:items-start pt-5 lg:pt-0 lg:ml-8 xl:ml-16 4xl:ml-16">
                  <h6 className="text-lg font-normal enfinity-header-font text-[#737373] text-center lg:text-left">
                    Enfinity Services
                  </h6>
                  <div className="flex flex-col gap-3 text-base font-normal leading-6 items-center lg:items-start">
                    <a
                      href="https://enfinipay.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      EnfiniPay
                    </a>
                    <a
                      href="https://enfiniboost.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      EnfiniBoost
                    </a>
                    <a
                      href="https://enfinishield.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      EnfiniShield
                    </a>
                    <a
                      href="https://enfinitask.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      EnfiniTask
                    </a>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="flex flex-col gap-3 items-center lg:items-start pt-5 lg:pt-0 lg:ml-4 xl:ml-14">
                  <h6 className="text-lg font-normal enfinity-header-font text-[#737373] text-center lg:text-left">
                    Quick Links
                  </h6>
                  <div className="flex flex-col gap-3 text-base font-normal leading-6 items-center lg:items-start">
                    <p
                      className="cursor-pointer hover:underline enfinity-subtext-font"
                      onClick={() =>
                        isHome
                          ? scrollTo("OurServices")
                          : handleNavClick("/", "OurServices")
                      }
                    >
                      Our Services
                    </p>
                    <p
                      className="cursor-pointer hover:underline enfinity-subtext-font"
                      onClick={() =>
                        isEnfiniBoost || isEnfiniPay || isEnfiniShield
                          ? location.pathname === aboutUsPath
                            ? scrollTo("about-us")
                            : navigate(aboutUsPath)
                          : isHome
                            ? scrollTo("about-us")
                            : handleNavClick("/", "about-us")
                      }
                    >
                      About Us
                    </p>
                    <p
                      className="cursor-pointer hover:underline enfinity-subtext-font"
                      onClick={() =>
                        isEnfiniBoost || isEnfiniPay || isEnfiniShield
                          ? location.pathname === caseStudiesPath
                            ? scrollTo("success-stories")
                            : navigate(caseStudiesPath)
                          : isHome
                            ? scrollTo("success-stories")
                            : handleNavClick("/", "success-stories")
                      }
                    >
                      Case Studies
                    </p>
                  </div>
                </div>

                {/* Payments */}
                <div className="flex flex-col gap-3 items-center lg:items-start pt-5 lg:pt-0 lg:ml-8 xl:ml-16 4xl:ml-16">
                  <h6 className="text-lg font-normal enfinity-header-font text-[#737373] text-center lg:text-left">
                    Payments
                  </h6>
                  <div className="flex flex-col gap-3 text-base font-normal leading-6 items-center lg:items-start">
                    <a
                      href="https://enfinipay.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      Daily Pay
                    </a>
                    <a
                      href="https://enfinipay.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      Early Pay
                    </a>
                    <a
                      href="https://enfinipay.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      Advance Pay
                    </a>
                    <a
                      href="https://enfinipay.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline enfinity-subtext-font"
                    >
                      Crypto Withdrawals
                    </a>
                  </div>
                </div>

                {/* Resources */}
                <div className="flex flex-col gap-3 items-center lg:items-start pt-5 lg:pt-0 lg:ml-4 xl:ml-14 4xl:ml-14">
                  <h6 className="text-lg font-normal enfinity-header-font text-[#737373] text-center lg:text-left">
                    Resources
                  </h6>
                  <div className="flex flex-col gap-3 text-base font-normal leading-6 items-center lg:items-start">
                    <p
                      className="cursor-pointer hover:underline enfinity-subtext-font"
                      onClick={() =>
                        isHome ? scrollTo("faqs") : handleNavClick("/", "faqs")
                      }
                    >
                      FAQs
                    </p>
                    <p
                      className="cursor-pointer hover:underline enfinity-subtext-font"
                      onClick={() =>
                        location.pathname === contactUsPath
                          ? scrollTo("get-in-touch")
                          : navigate(`${contactUsPath}#get-in-touch`)
                      }
                    >
                      Contact Us
                    </p>
                    <p
                      className="cursor-pointer hover:underline enfinity-subtext-font"
                      onClick={() =>
                        location.pathname === contactUsPath
                          ? scrollTo("get-in-touch")
                          : navigate(`${contactUsPath}#get-in-touch`)
                      }
                    >
                      Support
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider - Figma line */}
        <div className="h-px bg-white/20 w-full mb-8" />

        {/* Bottom Section - badge + copyright (same for all pages) */}
        <div className="flex flex-col items-center justify-center gap-6 text-white text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-3">
            {socialPlatforms.map((platform, index) =>
              platform.action ? (
                <div
                  key={index}
                  className="rounded-lg w-14 md:w-16 h-12 flex justify-center items-center bg-transparent border border-white"
                  onClick={platform.action}
                >
                  <img
                    src={platform.icon}
                    className="w-6 h-6"
                    alt={platform.name}
                  />
                </div>
              ) : (
                <a
                  key={index}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="rounded-lg w-14 md:w-16 h-12 flex justify-center items-center bg-transparent border border-white">
                    <img
                      src={platform.icon}
                      className="w-6 h-6"
                      alt={platform.name}
                    />
                  </div>
                </a>
              )
            )}
          </div>
          <a
            href={current.link || "#"}
            target={current.link && current.link !== "#" ? "_blank" : undefined}
            rel={
              current.link && current.link !== "#"
                ? "noopener noreferrer"
                : undefined
            }
          >
            <div className="relative h-14 min-w-[220px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className={`absolute left-0 top-0 flex items-center gap-2 rounded-full ${current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner" ? "py-2" : "py-1"} px-4 border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge`}
                >
                  <img
                    src={current.icon}
                    alt={current.name}
                    className={
                      current.name === "Crypto Payout Agency" ||
                      current.name === "Microsoft Certified Partner"
                        ? "h-6 w-6"
                        : current.name === "Official Snap Partner"
                          ? "h-8 w-6"
                          : "h-8 w-8"
                    }
                  />
                  <span className="xxs:text-xs xs:text-sm text-base">
                    {current.name}
                  </span>
                  <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
                </motion.div>
              </AnimatePresence>
            </div>
          </a>
          <p className="text-base font-normal text-white enfinity-subtext-font lg:hidden">
            © 2026 Enfinity. All rights reserved. | Official YouTube Technology
            Partner & Creators Services Provider
          </p>
          <p className="text-base font-normal text-white enfinity-subtext-font hidden lg:block">
            © 2026 Enfinity. All rights reserved. | Official YouTube Technology
            Partner & Creators Services Provider
          </p>
        </div>
      </div>
      <ToastModal
        showToast={showSubscribeToast}
        setShowToast={setShowSubscribeToast}
        form="newsletter"
        type={subscribeToastType}
      />
    </div>
  );
}
